import { useId, useState } from "react";
import { Icon } from "../../components/Icon";
import { Sheet } from "../../components/Sheet";
import type { CompareCell, CompareRow } from "./compareRows";
import type { BringBackOutcome, BringBackState } from "./useBringBack";
import { fmtCompareDate } from "./compareModel";

/**
 * Look back's card: Then beside Now for whatever the card is on — the dial, a section, an action point with
 * its items — changed rows first, unchanged ones folded away, and "Bring back" on anything that can be
 * copied. Copying is one way only: Then → Now, as new changes made today. Then never changes.
 */
export function CompareCard({ rows: given, day, bring, loading, ahead = false }: {
  /** KEY-890: the other dial is AHEAD — it says so, and nothing can be brought back from a day that hasn't come. */
  ahead?: boolean;
  rows: CompareRow[];
  day: string;
  bring: BringBackState;
  /** Then is still being replayed: skeleton lines in its column, Now as usual. */
  loading?: boolean;
}) {
  const date = fmtCompareDate(day);
  const rows = ahead ? given.map((r) => ({ ...r, bringBack: undefined })) : given;
  const changed = rows.filter((r) => r.changed);
  const unchanged = rows.filter((r) => !r.changed);
  const bringable = changed.filter((r) => r.bringBack);
  const [confirming, setConfirming] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  return (
    <section className="kd-compare-card" aria-label={ahead ? `Now and ahead (${date})` : `Then (${date}) and Now`}>
      <div className="kd-compare-head" aria-hidden="true">
        <span />
        <span className="kd-compare-head-then"><Icon name={ahead ? "calendar" : "history"} size={14} /> {ahead ? "Ahead" : "Then"} · {date}</span>
        <span className="kd-compare-head-now"><span className="kd-live-dot" /> Now</span>
      </div>

      {changed.length === 0 && !loading && (
        <p className="kd-compare-none">{ahead ? `Nothing here changes by itself by ${date}.` : `Nothing here has changed since ${date}.`}</p>
      )}
      {changed.map((row) => (
        <RowView key={row.id} row={row} loading={loading} date={date} busy={bring.busy}
          preview={previewId === row.id} onPreview={(on) => setPreviewId(on ? row.id : null)}
          onBringBack={() => void bring.bringBack([row])} />
      ))}

      {unchanged.length > 0 && (
        <details className="kd-compare-unchanged">
          <summary>
            {unchanged.length} unchanged · {unchanged.slice(0, 4).map((r) => r.label).join(", ")}{unchanged.length > 4 ? "…" : ""}
          </summary>
          {unchanged.map((row) => (
            <RowView key={row.id} row={row} loading={loading} date={date} busy={bring.busy} preview={false} onPreview={() => {}} onBringBack={() => {}} />
          ))}
        </details>
      )}

      {bring.last && <Outcome last={bring.last} date={date} bring={bring} />}

      {bringable.length > 0 && (
        <footer className="kd-compare-foot">
          <button type="button" className="kd-focusable kd-compare-bulk" disabled={bring.busy || loading} onClick={() => setConfirming(true)}>
            <Icon name="restore" size={15} /> Bring back all {bringable.length} value{bringable.length === 1 ? "" : "s"} as they were
          </button>
          <span>Copies Then → Now as new changes today. History is never rewritten.</span>
        </footer>
      )}

      {confirming && (
        <BringBackDialog
          rows={changed}
          date={date}
          busy={bring.busy}
          onCancel={() => setConfirming(false)}
          onConfirm={async (chosen) => {
            await bring.bringBack(chosen);
            setConfirming(false);
          }}
        />
      )}
    </section>
  );
}

function Cell({ cell, then, loading }: { cell: CompareCell; then?: boolean; loading?: boolean }) {
  if (loading && then) return <span className="kd-compare-skeleton" aria-label="Loading" />;
  return (
    <span className={cell.empty ? "kd-compare-empty" : undefined}>
      {cell.colour && <span className="kd-compare-dot" style={{ background: cell.colour }} aria-hidden="true" />}
      {cell.text}
      {cell.overdue && <span className="kd-compare-overdue"> · {cell.overdue}</span>}
    </span>
  );
}

function RowView({ row, loading, date, busy, preview, onPreview, onBringBack }: {
  row: CompareRow;
  loading?: boolean;
  date: string;
  busy: boolean;
  preview: boolean;
  onPreview: (on: boolean) => void;
  onBringBack: () => void;
}) {
  const reasonId = useId();
  return (
    <div className={`kd-compare-row${row.changed ? " is-changed" : ""}`}>
      <div className="kd-compare-label">
        {row.label}
        {row.changed && (row.emptyThen
          ? <span className="kd-chip-changed is-empty">Empty then</span>
          : <span className="kd-chip-changed">Changed</span>)}
      </div>
      <div className="kd-compare-cells">
        <div className="kd-compare-cell is-then">
          <Cell cell={row.then} then loading={loading} />
          {row.bringBack && !loading && (
            <button
              type="button"
              className="kd-focusable kd-bring-back"
              disabled={busy}
              aria-label={`Bring back: ${row.bringBack.describe} today`}
              title={`Bring back → Now becomes “${row.then.text}”, logged today by you`}
              onMouseEnter={() => onPreview(true)}
              onMouseLeave={() => onPreview(false)}
              onFocus={() => onPreview(true)}
              onBlur={() => onPreview(false)}
              onClick={onBringBack}
            >
              <Icon name="restore" size={15} />
            </button>
          )}
          {row.blocked && !loading && (
            <>
              <button type="button" className="kd-bring-back" disabled aria-describedby={reasonId} aria-label="Bring back isn't available">
                <Icon name="restore" size={15} />
              </button>
              <span id={reasonId} role="tooltip" className="kd-sr-only">{row.blocked}</span>
            </>
          )}
          {row.rollup && row.changed && <span className="kd-compare-rollup">rollup, no copy</span>}
        </div>
        <div className={`kd-compare-cell is-now${preview ? " is-incoming" : ""}`}>
          {preview ? <Cell cell={row.then} /> : <Cell cell={row.now} />}
        </div>
      </div>
      {row.blocked && <p className="kd-compare-blocked">{row.blocked}</p>}
      <span className="kd-sr-only">{`Then, ${date}: ${row.then.text}. Now: ${row.now.text}.`}</span>
    </div>
  );
}

/** After a Bring back: the toast with Undo, or — when some were refused — which, why, and a retry. */
function Outcome({ last, date, bring }: { last: BringBackOutcome[]; date: string; bring: BringBackState }) {
  const ok = last.filter((o) => o.ok);
  const refused = last.filter((o) => !o.ok);
  if (refused.length === 0) {
    return (
      <div className="kd-compare-toast" role="status">
        <span>
          {ok.length === 1 ? `${ok[0].row.label} brought back from ${date}` : `${ok.length} values brought back from ${date}`}
        </span>
        <button type="button" className="kd-focusable" disabled={bring.busy} onClick={() => void bring.undo()}>Undo</button>
        <button type="button" className="kd-focusable" aria-label="Dismiss" onClick={bring.dismiss}><Icon name="x" size={13} /></button>
      </div>
    );
  }
  return (
    <div className="kd-compare-partial" role="alert">
      <p>
        {ok.length} of {last.length} value{last.length === 1 ? "" : "s"} brought back.
        {ok.length > 0 && <> {ok.map((o) => o.row.label).join(", ")} {ok.length === 1 ? "is" : "are"} done;</>}
        {" "}{refused.map((o) => `${o.row.label} was refused: ${o.reason}`).join(" ")}
        {ok.length > 0 && <> The {ok.length === 1 ? "change is" : `${ok.length} changes are`} kept.</>}
      </p>
      <div>
        {refused.map((o) => (
          <button key={o.row.id} type="button" className="kd-focusable" disabled={bring.busy} onClick={() => void bring.bringBack([o.row])}>
            Try {o.row.label} again
          </button>
        ))}
        {ok.length > 0 && <button type="button" className="kd-focusable" disabled={bring.busy} onClick={() => void bring.undo()}>Undo</button>}
        <button type="button" className="kd-focusable" onClick={bring.dismiss}>Dismiss</button>
      </div>
    </div>
  );
}

/** The bulk confirm: every changed value, ticked unless it can't be copied, then one button. */
export function BringBackDialog({ rows, date, busy, onCancel, onConfirm }: {
  rows: CompareRow[];
  date: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (chosen: CompareRow[]) => void | Promise<void>;
}) {
  const [ticked, setTicked] = useState<Set<string>>(() => new Set(rows.filter((r) => r.bringBack).map((r) => r.id)));
  const chosen = rows.filter((r) => r.bringBack && ticked.has(r.id));
  const n = chosen.length;
  return (
    <Sheet
      title={`Bring back ${n} value${n === 1 ? "" : "s"} as they were on ${date}?`}
      subtitle="Then → Now · Copies one way only. Then never changes."
      onClose={onCancel}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="kd-focusable kd-btn-quiet" onClick={onCancel}>Cancel</button>
          <button type="button" className="kd-focusable kd-compare-bulk" disabled={busy || n === 0} onClick={() => void onConfirm(chosen)}>
            Bring back {n} value{n === 1 ? "" : "s"}
          </button>
        </div>
      }
    >
      <ul className="kd-bring-list">
        {rows.filter((r) => r.rollup !== true).map((r) => (
          <li key={r.id}>
            <label className={r.bringBack ? undefined : "is-disabled"}>
              <input
                type="checkbox"
                checked={!!r.bringBack && ticked.has(r.id)}
                disabled={!r.bringBack}
                onChange={(e) => setTicked((prev) => {
                  const next = new Set(prev);
                  if (e.target.checked) next.add(r.id);
                  else next.delete(r.id);
                  return next;
                })}
              />
              <span className="kd-bring-label">{r.label}</span>
              <span>{r.then.text}</span>
              <span aria-hidden="true">→</span>
              <s>{r.now.text}</s>
            </label>
            {r.blocked && <p className="kd-compare-blocked">{r.blocked}</p>}
          </li>
        ))}
      </ul>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        Each value is saved as a normal change made today by you, one after another, and shows in Activity as
        brought back from {date}. Nothing in history is rewritten. Items that aren&rsquo;t ticked are left as they are now.
      </p>
    </Sheet>
  );
}
